import { Steps } from 'antd';
import classNames from 'classnames';
import { fromPairs, get, noop } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { numberOr } from '../../common/utils/numberUtils';
import { FAIcon, FATypes } from '../adapters/fontAwesomeAdapters';
import { useResponsiveQueries } from '../responsive/responsiveQueries';
import SegmentedControl from './SegmentedControl';
import { SlidingContent } from './SlidingContent';
import { useStepsWithContentStyles } from './StepsWithContent.styles';

function getStepsItems(steps, currentIndex, styles) {
  return steps.map(({ name, titleId, disabled }, index) => {
    let icon;

    if (index < currentIndex) {
      // Finished step
      icon = <FAIcon icon="check" className={styles.stepsFaIcon} />;
    } else if (index === currentIndex) {
      // Active step
      icon = (
        <FAIcon
          icon="circle"
          type={FATypes.SOLID}
          className={styles.stepsFaIcon}
        />
      );
    }
    return {
      key: name,
      title: <FormattedMessage id={titleId} />,
      icon,
      disabled,
    };
  });
}

const StepsCarousel = ({ step, steps, goToNext }) => (
  <SlidingContent
    listenToSelected
    selected={step}
    data={steps}
    sharedProps={{ goToNext }}
    autoHeight
  />
);
export function StepsWithContent({
  className,
  stepsClassName,
  segmentedControlClassName,
  step,
  setStep,
  steps,
  currentIndex,
  goToNext,
  clickable,
}) {
  const media = useResponsiveQueries();
  const isLg = media.lg || media.xl || media.xxl;
  const { styles } = useStepsWithContentStyles();

  return (
    <div className={classNames('StepsWithContent', className)}>
      {isLg ? (
        <Steps
          className={classNames(styles.steps, stepsClassName)}
          current={currentIndex}
          items={getStepsItems(steps, currentIndex, styles)}
          onChange={clickable ? i => setStep(steps[i].name) : undefined}
          direction="horizontal"
          labelPlacement="vertical"
          size="default"
          status="process"
        />
      ) : (
        <SegmentedControl
          className={segmentedControlClassName}
          value={step}
          onChange={clickable ? setStep : noop}
          options={steps.map(({ shortTitleId, name, disabled }) => ({
            labelId: shortTitleId,
            value: name,
            disabled,
          }))}
        />
      )}
      <StepsCarousel
        steps={steps}
        step={step}
        currentIndex={currentIndex}
        goToNext={goToNext}
      />
    </div>
  );
}

export function isLastStep({ steps, currentIndex }) {
  return currentIndex === steps.length - 1;
}
export function isFirstStep({ currentIndex }) {
  return currentIndex === 0;
}

function useStepControls({ step, setStep, setInitStep, steps: rawSteps }) {
  const steps = rawSteps.map((info, i) => ({
    ...info,
    index: i,
  }));
  const stepsByName = fromPairs(steps.map(s => [s.name, s]));
  const currentIndex = numberOr(get(stepsByName, `${step}.index`), 0);
  const goToNext = useCallback(() => {
    if (!isLastStep({ steps, currentIndex })) {
      setStep(steps[currentIndex + 1].name);
    }
  }, [currentIndex, setStep, steps]);
  const goToPrev = useCallback(() => {
    if (!isFirstStep({ steps, currentIndex })) {
      setStep(steps[currentIndex - 1].name);
    }
  }, [currentIndex, setStep, steps]);

  // Set step if not defined
  useEffect(() => {
    if (!step || !stepsByName[step]) {
      const initSetter = setInitStep || setStep;
      initSetter(steps[0].name);
    }
  }, [setInitStep, setStep, step, steps, stepsByName]);

  return { steps, currentIndex, goToNext, goToPrev };
}

export function useInternalStepsState(steps) {
  const [step, setStep] = useState();
  return { ...useStepControls({ step, setStep, steps }), step, setStep };
}
